<script setup>
import { submitNewPaymentMethod } from '#common/services/paymentServices'
import { scrollToElement } from '#common/utils/form'
import { isApplePaySupported } from '#common/utils/payment'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  totalsObject: {
    type: Object,
    required: true,
  },
})

const { $auth, $axios } = useNuxtApp()
const emit = defineEmits(['commit'])
const ccForm = ref(null)
const showNewPayment = ref(false)
const savePaymentDetails = ref(true)
const oneTimePaymentMethod = ref(null)
const errors = ref([])
const submitting = ref(false)
const paypalButtonStyle = ref({
  size: 'large',
  color: 'white',
  shape: 'rect',
  label: 'paypal',
  tagline: 'false',
})
const applePayButtonStyle = ref({
  buttonstyle: 'white-outline',
  type: 'plain',
  buttonWidth: '350px',
  buttonHeight: '45px',
})
const selectedPaymentOption = ref('paymentOptionCreditCard')
const selectedPaymentMethod = ref(null)
const hostedFieldsStyles = ref({
  'input,select': {
    'font-family': '"Nunito Sans", sans-serif',
    'font-size': '1rem',
    padding: '1.3rem 1rem 0.4rem',
    '-webkit-appearance': 'none',
    outline: 'none',
  },
  '.invalid': {
    color: 'red',
  },
  '.valid': {
    color: 'inherit',
  },
  ':focus': {
    color: 'inherit',
  },
})

const customPaymentTheme = computed(() => {
  return {
    showPlaceholders: true,
    cardholderName: {
      classes: '',
    },
    billingSameAsShippingCheckbox: {
      classes: 'checkmark-form-input',
    },
    savePaymentDetailsCheckbox: {
      classes: 'checkmark-form-input',
    },
    makeDefaultCheckbox: {
      classes: 'checkmark-form-input',
    },
    billingAddress: {
      line1: {
        classes: '',
      },
      line2: {
        classes: '',
      },
      city: {
        classes: 'medium-4',
      },
      state: {
        classes: 'medium-4',
      },
      zip: {
        classes: '',
      },
    },
  }
})

const paymentMethods = usePaymentMethods()
const memberships = useMemberships()
const sectionState = computed(() => memberships.sectionState)

const drawerOpen = computed(
  () => sectionState.value.paymentInformation.drawerOpen,
)
const loggedIn = computed(() => $auth.loggedIn || false)
const paypalMethods = computed(() =>
  _.filter(paymentMethods.userPaymentMethods, (x) => x.is_paypal),
)
const applePayMethods = computed(() =>
  _.filter(paymentMethods.userPaymentMethods, (x) => x.is_applepay),
)
const showGrantTotal = computed(
  () =>
    !_.isEmpty(props.totalsObject) &&
    props.totalsObject.grand_total !== undefined,
)
const creditCardMethods = computed(() => {
  return _.filter(paymentMethods.userPaymentMethods, (x) => x.is_cc)
})
const applePaySupported = computed(() => {
  if (import.meta.client) {
    return isApplePaySupported(window)
  }
  return false
})
const showApplePay = computed(
  () => applePaySupported.value || applePayMethods.value.length > 1,
)
const showMakeDefaultCheckbox = computed(
  () => creditCardMethods.value.length > 1,
)
const selectedPaymentMethodSummaryString = computed(() => {
  if (oneTimePaymentMethod.value) {
    if (selectedPaymentOption.value === 'paymentOptionCreditCard') {
      return `${oneTimePaymentMethod.value.details.cardType} •••• •••• •••• ${oneTimePaymentMethod.value.details.lastFour}`
    } else if (selectedPaymentOption.value === 'paymentOptionPayPal') {
      return `PayPal (${oneTimePaymentMethod.value.details.email})`
    } else if (selectedPaymentOption.value === 'paymentOptionApplePay') {
      return `Apple Pay - ${oneTimePaymentMethod.value.paymentMethod.displayName}`
    }
  }
  if (selectedPaymentMethod.value) {
    const obj = paymentMethods.userPaymentMethods.find(
      (x) => x.card_id === selectedPaymentMethod.value,
    )
    if (obj) {
      return obj.summary_string
    }
  }
  return ''
})
const sectionClasses = computed(() => {
  const classes = ['checkout-details-section']
  if (!drawerOpen.value) {
    classes.push('edit-section')
  }
  return classes
})
async function initializePayments() {
  memberships.setLoading(true)
  if ($auth.loggedIn) {
    const methods = await paymentMethods.loadPaymentMethods()
    _.forEach(methods, (method) => {
      if (!selectedPaymentMethod.value && method.is_default) {
        if (method.is_paypal) {
          selectedPaymentOption.value = 'paymentOptionPayPal'
        } else if (method.is_applepay) {
          selectedPaymentOption.value = 'paymentOptionApplePay'
        } else {
          selectedPaymentOption.value = 'paymentOptionCreditCard'
        }
        selectedPaymentMethod.value = method.card_id
      }
    })
  } else {
    showNewPayment.value = true
  }
  memberships.setLoading(false)
}
onMounted(async () => {
  await initializePayments()
})
onActivated(async () => {
  await initializePayments()
})

async function commit() {
  errors.value = []
  if (
    !$auth.loggedIn &&
    selectedPaymentOption.value === 'paymentOptionCreditCard' &&
    !(await submitNewCCForm())
  ) {
    return
  }
  if (!selectedPaymentMethod.value) {
    errors.value.push('Please fill out a payment method')
    return
  }
  memberships.setSectionState({
    section: 'paymentInformation',
    value: {
      drawerOpen: false,
      selectedPaymentOption: selectedPaymentOption.value,
      selectedPaymentMethod: selectedPaymentMethod.value,
      savePaymentDetails: savePaymentDetails.value,
    },
  })
  emit('commit')
}
function openDrawer() {
  errors.value = []
  memberships.setSectionDrawerOpen({
    section: 'paymentInformation',
    value: true,
  })
}
function closeDrawer() {
  errors.value = []
  memberships.setSectionDrawerOpen({
    section: 'paymentInformation',
    value: false,
  })
}
function onSummaryEditClicked(method) {
  selectedPaymentMethod.value = method.card_id
  showNewPayment.value = false
}
function cancelClicked() {
  showNewPayment.value = false
}
function paymentMethodIsSelected(paymentMethod) {
  if (selectedPaymentMethod.value) {
    return selectedPaymentMethod.value === paymentMethod.card_id
  } else {
    return false
  }
}
function scrollToCardById(cardID) {
  cancelClicked()
  scrollToElement(nextTick, `#${cardID}`)
}
function addNewCreditCardClicked() {
  selectedPaymentMethod.value = null
  showNewPayment.value = true
}
async function onCardUpdated(cardId) {
  await paymentMethods.reloadPaymentMethods()
  scrollToCardById(cardId)
}

async function braintreeSubmit(payload, newSelectedPaymentOption, makeDefault) {
  memberships.setLoading(true)
  try {
    const resp = await submitNewPaymentMethod($axios, {
      token: payload.nonce,
      make_default: makeDefault,
    })
    // Update list of payment accounts
    await paymentMethods.reloadPaymentMethods()
    selectedPaymentOption.value = newSelectedPaymentOption
    selectedPaymentMethod.value = resp.data.payment_token
  } catch (e) {
    if (e.response && e.response.data) {
      errors.value = [e.response.data.display_error]
    }
    Sentry.captureException(e)
    console.error('Error submitting braintree payment method', e.response)
  } finally {
    memberships.setLoading(false)
  }
}
async function payPalSubmit(payload) {
  if (!$auth.loggedIn) {
    selectedPaymentOption.value = 'paymentOptionPayPal'
    selectedPaymentMethod.value = payload.nonce
    oneTimePaymentMethod.value = payload
    return
  }
  return await braintreeSubmit(payload, 'paymentOptionPayPal', true)
}
async function applePaySubmit(payload) {
  if (!$auth.loggedIn) {
    selectedPaymentOption.value = 'paymentOptionApplePay'
    selectedPaymentMethod.value = payload.nonce
    oneTimePaymentMethod.value = payload
    return
  }
  return await braintreeSubmit(payload, 'paymentOptionApplePay', false)
}

function savePaymentDetailsClicked(value) {
  savePaymentDetails.value = value
}

async function submitNewCCForm() {
  memberships.setLoading(true)

  const tokenizedData = await ccForm.value.validateAndGetToken()

  if (tokenizedData === null) {
    memberships.setLoading(false)
    return false
  }

  if (!$auth.loggedIn || !savePaymentDetails.value) {
    selectedPaymentOption.value = 'paymentOptionCreditCard'
    selectedPaymentMethod.value = tokenizedData.token
    oneTimePaymentMethod.value = tokenizedData
    if (!savePaymentDetails.value) {
      commit()
    }
    memberships.setLoading(false)
    return true
  }

  try {
    const resp = await submitNewPaymentMethod($axios, tokenizedData)
    await paymentMethods.reloadPaymentMethods()
    selectedPaymentOption.value = 'paymentOptionCreditCard'
    selectedPaymentMethod.value = resp.data.payment_token
    showNewPayment.value = false
    ccForm.value.clear()
    return true
  } catch (e) {
    Sentry.captureException(e)
    console.error('Failed adding card', e)
    if (e.response && e.response.data) {
      errors.value = e.response.data.display_error
    } else {
      Sentry.captureException(e)
      console.error('Error submitting braintree payment method', e)
    }
    return false
  } finally {
    memberships.setLoading(false)
  }
}
function truncatePayPalMethod(email) {
  if (email.length > 28) {
    return email.slice(0, 25) + '...'
  }
  return email
}
function onPaymentSelected(method) {
  selectedPaymentMethod.value = method.card_id
  showNewPayment.value = false
}
function tabChange(newPaymentOption) {
  // Deselect payment method on tab change
  if (newPaymentOption !== selectedPaymentOption.value)
    selectedPaymentMethod.value = null
  if (!$auth.loggedIn) {
    oneTimePaymentMethod.value = null
  }
}
</script>

<template>
  <CheckoutSection
    :id="id"
    section-title="Payment"
    :show-section-number="true"
    :section-number="4"
    :enabled="true"
    :drawer-open="drawerOpen"
    :show-change-link="true"
    :errors="errors"
    change-text="Edit"
    :classes="sectionClasses"
    @drawerOpen="openDrawer"
    @drawerClosed="closeDrawer"
  >
    <template #collapsed>
      <div class="checkout-section-summary">
        <ul>
          <li>{{ selectedPaymentMethodSummaryString }}</li>
        </ul>
      </div>
    </template>
    <template #expanded>
      <div class="checkout-section-content">
        <div class="checkout-form">
          <div class="tabset">
            <input
              id="credit-card"
              v-model="selectedPaymentOption"
              type="radio"
              name="paymentRadio"
              value="paymentOptionCreditCard"
              @click="tabChange('paymentOptionCreditCard')"
            />
            <label for="credit-card">
              Credit Card
              <span class="image">
                <img src="~/assets/images/svg/logo-visa.svg" alt="Visa Logo" />
                <img
                  src="~/assets/images/svg/logo-mastercard.svg"
                  alt="Mastercard Logo"
                />
                <img
                  src="~/assets/images/svg/logo-amex.svg"
                  alt="American Express Logo"
                />
              </span>
            </label>
            <!-- Tab 2 -->
            <input
              id="paypal-radio"
              v-model="selectedPaymentOption"
              type="radio"
              name="paymentRadio"
              value="paymentOptionPayPal"
              @click="tabChange('paymentOptionPayPal')"
            />
            <label for="paypal-radio">
              PayPal
              <span class="image">
                <img
                  src="~/assets/images/svg/logo-paypal.svg"
                  alt="PayPal Logo"
                />
              </span>
            </label>
            <!-- Tab 3 -->
            <input
              v-if="showApplePay"
              id="applepay"
              v-model="selectedPaymentOption"
              type="radio"
              name="paymentRadio"
              value="paymentOptionApplePay"
              @click="tabChange('paymentOptionApplePay')"
            />
            <label v-if="showApplePay" for="applepay">
              ApplePay
              <span class="image">
                <img
                  src="~/assets/images/svg/logo-apple.svg"
                  alt="ApplePay Logo"
                />
              </span>
            </label>
            <!-- Tab Panels -->
            <div class="tab-panels">
              <section class="tab-panel">
                <div
                  v-if="
                    loggedIn &&
                    selectedPaymentOption === 'paymentOptionCreditCard'
                  "
                  class="radio-grid stacked small payment-suboptions floating editable-summary"
                  style="margin-top: 0 !important"
                >
                  <ul id="cc-method-list">
                    <template
                      v-for="method in creditCardMethods"
                      :key="method.card_id"
                    >
                      <CheckoutCreditCardSummaryFK
                        :id="method.card_id"
                        :ref="'cc-summary-' + method.card_id"
                        :payment-method="method"
                        name="paymentMethodOption"
                        :checked="paymentMethodIsSelected(method)"
                        :show-make-default-checkbox="showMakeDefaultCheckbox"
                        :force-default="!showMakeDefaultCheckbox"
                        :editable="!method.one_time"
                        :card="method"
                        :hosted-fields-styles="hostedFieldsStyles"
                        :floating="true"
                        id-prefix="edit"
                        address-line-one-id="edit-payment-address-line-1"
                        @paymentSelected="onPaymentSelected(method)"
                        @editClicked="onSummaryEditClicked(method)"
                        @cardUpdated="onCardUpdated"
                        @cardEditClosed="scrollToCardById"
                      />
                    </template>
                    <li v-if="!showNewPayment">
                      <button
                        class="add-new-cc left"
                        @click="addNewCreditCardClicked"
                      >
                        Add New Credit Card
                      </button>
                    </li>
                  </ul>
                </div>
                <div v-if="showNewPayment" class="floating new-form">
                  <ClientOnly>
                    <CreditCardFormFK
                      ref="ccForm"
                      include-number
                      submit-button-class="primary continue"
                      :hide-buttons="true"
                      :disabled="submitting"
                      :amount="Number(totalsObject.grand_total) || 0.01"
                      :show-make-default-checkbox="loggedIn"
                      :show-save-payment-details-checkbox="loggedIn"
                      :save-payment-details-default="true"
                      :show-pay-pal-button="false"
                      :custom-theme="customPaymentTheme"
                      address-line-one-id="input-new-payment-address-line-1"
                      :floating="true"
                      id-prefix="new"
                      @payPalTokenized="submit($event.token, true)"
                      @applePayTokenized="submit($event.token, true)"
                      @savePaymentDetailsClicked="savePaymentDetailsClicked"
                    >
                      <template #nameForm="nameSlotProps">
                        <FloatingNameFormFK
                          id-prefix="new"
                          :validation-rules="nameSlotProps.validationRules"
                        />
                      </template>
                      <template #hostedRowForm="hostedSlotProps">
                        <FloatingHostedFieldsForm
                          :tokenize-errors="hostedSlotProps.tokenizeErrors"
                          :include-number="hostedSlotProps.includeNumber"
                          :initial="hostedSlotProps.initial"
                        />
                      </template>
                      <template #addressForm="addressSlotProps">
                        <FloatingAddressFormFK
                          id-prefix="new"
                          :state-options="addressSlotProps.stateOptions"
                          @addressAutocomplete="
                            addressSlotProps.autoCompletePlaceChanged
                          "
                        />
                      </template>
                    </CreditCardFormFK>
                    <div
                      v-if="loggedIn"
                      class="standard-form form-container"
                      style="margin-left: auto"
                    >
                      <CheckoutSectionSave
                        :save-disabled="false"
                        save-button-text="Save"
                        template-class="row button-group save-cancel"
                        @saveClicked="submitNewCCForm"
                        @cancelClicked="cancelClicked"
                      ></CheckoutSectionSave>
                    </div>
                  </ClientOnly>
                </div>
              </section>
              <section class="tab-panel" style="margin-bottom: 25px">
                <div
                  class="radio-grid stacked small payment-suboptions editable-summary"
                  style="margin-top: 0 !important"
                >
                  <ul>
                    <li
                      v-for="method in paypalMethods"
                      v-show="loggedIn"
                      :key="method.card_id"
                    >
                      <input
                        :id="method.card_id"
                        v-model="selectedPaymentMethod"
                        type="radio"
                        name="paymentMethodOption"
                        :value="method.card_id"
                      />
                      <label :for="method.card_id">
                        <strong :title="method.email">{{
                          truncatePayPalMethod(method.email)
                        }}</strong>
                      </label>
                    </li>
                    <li v-if="selectedPaymentOption === 'paymentOptionPayPal'">
                      <div>
                        <button
                          aria-label="Pay Pal"
                          style="
                            border: none;
                            background-color: unset;
                            width: unset;
                            margin: 0;
                            position: relative;
                            right: unset;
                          "
                        >
                          <PayPal
                            :amount="Number(totalsObject.grand_total) || 0.01"
                            :show-email="!loggedIn"
                            :button-style="paypalButtonStyle"
                            :increase-pay-pal-size="true"
                            @tokenized="payPalSubmit"
                          />
                        </button>
                        <div
                          v-if="
                            !loggedIn &&
                            oneTimePaymentMethod &&
                            oneTimePaymentMethod.type === 'PayPalAccount' &&
                            selectedPaymentOption === 'paymentOptionPayPal'
                          "
                          class="guest-paypal-email"
                          style="margin-top: 10px; padding-bottom: 10px"
                        >
                          <u>{{ oneTimePaymentMethod.details.email }}</u>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </section>
              <section v-if="showApplePay" class="tab-panel">
                <div
                  class="radio-grid stacked small payment-suboptions editable-summary"
                  style="margin-top: 0 !important"
                >
                  <ul>
                    <li
                      v-for="method in applePayMethods"
                      v-show="loggedIn"
                      :key="method.card_id"
                    >
                      <input
                        :id="method.card_id"
                        v-model="selectedPaymentMethod"
                        type="radio"
                        name="paymentMethodOption"
                        :value="method.card_id"
                      />
                      <label :for="method.card_id">
                        <strong
                          >{{ method.summary_string }}
                          <span
                            v-if="method.expiration_date"
                            class="cc-expiration"
                          >
                            <br />
                            expires {{ method.expiration_date }}
                          </span>
                        </strong>
                      </label>
                    </li>
                    <li v-if="applePaySupported">
                      <button
                        aria-label="Apple Pay"
                        class="add-new-cc apple-pay-button"
                        style="
                          border: none;
                          background-color: unset;
                          width: unset;
                        "
                      >
                        <ApplePay
                          style="padding-bottom: 25px"
                          :amount="Number(totalsObject.grand_total) || 0"
                          :button-style="applePayButtonStyle"
                          @tokenized="applePaySubmit"
                        />
                      </button>
                      <div
                        v-if="
                          oneTimePaymentMethod &&
                          oneTimePaymentMethod.type === 'ApplePayCard'
                        "
                      >
                        Apple Pay -
                        {{ oneTimePaymentMethod.paymentMethod.displayName }}
                      </div>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </div>

          <div class="row">
            <div class="columns">
              <h3 class="checkout-total">
                Order Total:
                <Price
                  v-if="showGrantTotal"
                  :amt="Number(totalsObject.grand_total)"
                /><span v-else>-</span>
              </h3>
            </div>
          </div>

          <div class="row">
            <div class="columns small-12 medium-9 large-6">
              <button type="submit" class="primary left" @click="commit">
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CheckoutSection>
</template>
